import type { Vec2 } from "../airport/AirportDefinition";
import type { PickRadii, ReleaseOutcome } from "../game/GameSession";

/** Touch pick radii, in screen pixels, widened from the mouse defaults. */
const TOUCH_AIRCRAFT_PIXELS = 22;
const TOUCH_PATH_PIXELS = 10;

/**
 * Wheel notch to zoom factor.
 *
 * Exponential rather than a step per event: a mouse wheel sends one ~100 px
 * notch, a trackpad sends a stream of small ones, and only a factor that
 * scales with the delta feels the same in both hands.
 */
const WHEEL_ZOOM_RATE = 0.0015;
/** A pinch below this many pixels of change is a hand resting, not a gesture. */
const PINCH_DEADZONE_PIXELS = 2;

export interface PointerTarget {
  grab(point: Vec2, radii: PickRadii): boolean;
  drag(point: Vec2): boolean;
  release(): ReleaseOutcome;
  hover(point: Vec2, radii: PickRadii): number | null;
  hoveredId: number | null;
}

export interface InputControllerOptions {
  readonly canvas: HTMLCanvasElement;
  readonly session: PointerTarget;
  /** Canvas-relative pixels to world units. */
  readonly toWorld: (x: number, y: number) => Vec2;
  /** World units covered by a screen distance, for touch tolerances. */
  readonly worldUnitsForPixels: (pixels: number) => number;
  readonly onGrab?: () => void;
  readonly onPathPoint?: () => void;
  readonly onRelease?: (outcome: ReleaseOutcome) => void;
  /** Multiplier to apply to the camera zoom; > 1 is closer. */
  readonly onZoom?: (factor: number) => void;
}

/**
 * Turns pointer events into path drawing.
 *
 * One gesture: press on an aircraft (or its existing path), drag to trace a
 * route, release to commit it. Touch gets a larger pick radius because a
 * fingertip is not a cursor.
 */
export class InputController {
  private drawing = false;
  /** Pointer that owns the gesture; every other pointer is ignored. */
  private activePointerId: number | null = null;
  private radii: PickRadii = {};
  /** Live finger positions, in client pixels, for the pinch. */
  private readonly touches = new Map<number, { x: number; y: number }>();
  private pinchDistance: number | null = null;

  constructor(private readonly options: InputControllerOptions) {
    const { canvas } = options;
    canvas.addEventListener("pointerdown", this.handlePointerDown);
    canvas.addEventListener("pointermove", this.handlePointerMove);
    canvas.addEventListener("pointerup", this.handlePointerUp);
    canvas.addEventListener("pointercancel", this.handlePointerUp);
    canvas.addEventListener("pointerleave", this.handlePointerLeave);
    // Not passive: the browser's own page zoom and rubber-band scroll would
    // otherwise fight the camera for the same gesture.
    canvas.addEventListener("wheel", this.handleWheel, { passive: false });
    canvas.style.touchAction = "none";
  }

  dispose(): void {
    const { canvas } = this.options;
    canvas.removeEventListener("pointerdown", this.handlePointerDown);
    canvas.removeEventListener("pointermove", this.handlePointerMove);
    canvas.removeEventListener("pointerup", this.handlePointerUp);
    canvas.removeEventListener("pointercancel", this.handlePointerUp);
    canvas.removeEventListener("pointerleave", this.handlePointerLeave);
    canvas.removeEventListener("wheel", this.handleWheel);
    this.touches.clear();
    this.pinchDistance = null;
  }

  private radiiFor(event: PointerEvent): PickRadii {
    if (event.pointerType !== "touch" && event.pointerType !== "pen") {
      return {};
    }
    return {
      aircraft: this.options.worldUnitsForPixels(TOUCH_AIRCRAFT_PIXELS),
      path: this.options.worldUnitsForPixels(TOUCH_PATH_PIXELS),
    };
  }

  private pointOf(event: PointerEvent): Vec2 {
    const bounds = this.options.canvas.getBoundingClientRect();
    return this.options.toWorld(
      event.clientX - bounds.left,
      event.clientY - bounds.top,
    );
  }

  /** Distance between the first two fingers down, or null if fewer. */
  private pinchSpan(): number | null {
    const [first, second] = [...this.touches.values()];
    if (!first || !second) {
      return null;
    }
    return Math.hypot(second.x - first.x, second.y - first.y);
  }

  private readonly handleWheel = (event: WheelEvent): void => {
    if (!this.options.onZoom) {
      return;
    }
    event.preventDefault();
    // Up (negative delta) is closer, matching every map on the platform.
    this.options.onZoom(Math.exp(-event.deltaY * WHEEL_ZOOM_RATE));
  };

  private readonly handlePointerDown = (event: PointerEvent): void => {
    if (event.button !== undefined && event.button !== 0) {
      return;
    }
    if (event.pointerType === "touch" || event.pointerType === "pen") {
      this.touches.set(event.pointerId, {
        x: event.clientX,
        y: event.clientY,
      });
      // A route being drawn keeps the gesture; the pinch only takes over when
      // both fingers landed on empty sky.
      this.pinchDistance = this.drawing ? null : this.pinchSpan();
    }
    // A second finger must not steal a route that is still being drawn: `grab`
    // overwrites the session's drawing state, which would abandon the first
    // aircraft mid-path and then feed it every move from both pointers.
    if (this.drawing) {
      return;
    }
    this.radii = this.radiiFor(event);
    if (!this.options.session.grab(this.pointOf(event), this.radii)) {
      return;
    }
    this.drawing = true;
    this.activePointerId = event.pointerId;
    this.options.onGrab?.();
    this.options.canvas.style.cursor = "grabbing";
    this.options.canvas.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  private readonly handlePointerMove = (event: PointerEvent): void => {
    if (this.touches.has(event.pointerId)) {
      this.touches.set(event.pointerId, {
        x: event.clientX,
        y: event.clientY,
      });
      const span = this.pinchSpan();
      if (this.pinchDistance !== null && span !== null) {
        if (Math.abs(span - this.pinchDistance) >= PINCH_DEADZONE_PIXELS) {
          this.options.onZoom?.(span / this.pinchDistance);
          this.pinchDistance = span;
        }
        event.preventDefault();
        return;
      }
    }

    const point = this.pointOf(event);
    if (!this.drawing) {
      const hovered = this.options.session.hover(point, this.radiiFor(event));
      this.options.canvas.style.cursor =
        hovered === null ? "default" : "pointer";
      return;
    }
    if (event.pointerId !== this.activePointerId) {
      return;
    }
    if (this.options.session.drag(point)) {
      this.options.onPathPoint?.();
    }
    event.preventDefault();
  };

  private readonly handlePointerUp = (event: PointerEvent): void => {
    if (this.touches.delete(event.pointerId) && this.touches.size < 2) {
      // One finger left: the next pinch starts from a fresh span rather than
      // jumping by whatever the lifted finger was doing.
      this.pinchDistance = null;
    }
    // Only the pointer that started the gesture may end it. Without this a
    // second finger lifting, or a right-click during a left-drag, commits the
    // half-drawn route and drops capture for the pointer still held down.
    if (!this.drawing || event.pointerId !== this.activePointerId) {
      return;
    }
    if (
      event.type === "pointerup" &&
      event.button !== undefined &&
      event.button !== 0
    ) {
      return;
    }
    this.drawing = false;
    this.activePointerId = null;
    const outcome = this.options.session.release();
    this.options.onRelease?.(outcome);
    this.options.session.hover(this.pointOf(event), this.radii);
    this.options.canvas.style.cursor =
      this.options.session.hoveredId === null ? "default" : "pointer";
    try {
      this.options.canvas.releasePointerCapture(event.pointerId);
    } catch {
      // The pointer may already be gone; nothing to release.
    }
    event.preventDefault();
  };

  private readonly handlePointerLeave = (): void => {
    if (this.drawing) {
      return;
    }
    this.options.session.hoveredId = null;
    this.options.canvas.style.cursor = "default";
  };
}
